import axios from 'axios';
import { httpClient } from '@/api/httpClient';
import {
  mapApiResourceToClientTreatmentPlan,
  mapApiResourceToClientTreatmentPlanExercise,
  mapApiResourceToExerciseCompliance,
  mapApiResourceToTreatmentPlan,
  mapApiResourceToTreatmentPlanVersionSummary,
  mapExerciseCompletionCreatePayload,
  mapTreatmentPlanWritePayload,
  type ClientTreatmentPlan,
  type ClientTreatmentPlanApiResource,
  type ClientTreatmentPlanExercise,
  type ClientTreatmentPlanExerciseApiResource,
  type ExerciseComplianceApiResource,
  type ExerciseCompliance,
  type ExerciseCompletionCreatePayload,
  type TreatmentPlan,
  type TreatmentPlanApiResource,
  type TreatmentPlanVersionSummary,
  type TreatmentPlanVersionSummaryApiResource,
  type TreatmentPlanWritePayload,
} from './treatmentPlansApi.types';

export async function saveTreatmentPlan(
  patientId: string,
  payload: TreatmentPlanWritePayload
): Promise<TreatmentPlan> {
  const response = await httpClient.put<TreatmentPlanApiResource>(
    '/patients/' + patientId + '/treatment-plan',
    mapTreatmentPlanWritePayload(payload)
  );
  return mapApiResourceToTreatmentPlan(response.data);
}

export async function fetchActiveTreatmentPlan(patientId: string): Promise<TreatmentPlan | null> {
  try {
    const response = await httpClient.get<TreatmentPlanApiResource>(
      '/patients/' + patientId + '/treatment-plan'
    );
    return mapApiResourceToTreatmentPlan(response.data);
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response !== undefined &&
      error.response.status === 404
    ) {
      return null;
    }
    throw error;
  }
}

export async function fetchTreatmentPlanVersions(
  patientId: string
): Promise<TreatmentPlanVersionSummary[]> {
  const response = await httpClient.get<TreatmentPlanVersionSummaryApiResource[]>(
    '/patients/' + patientId + '/treatment-plan/versions'
  );
  return response.data.map(mapApiResourceToTreatmentPlanVersionSummary);
}

export async function fetchTreatmentPlanVersion(planId: string): Promise<TreatmentPlan> {
  const response = await httpClient.get<TreatmentPlanApiResource>('/treatment-plans/' + planId);
  return mapApiResourceToTreatmentPlan(response.data);
}

export async function shareTreatmentPlan(patientId: string): Promise<TreatmentPlan> {
  const response = await httpClient.post<TreatmentPlanApiResource>(
    '/patients/' + patientId + '/treatment-plan/share'
  );
  return mapApiResourceToTreatmentPlan(response.data);
}

export async function unshareTreatmentPlan(patientId: string): Promise<TreatmentPlan> {
  const response = await httpClient.post<TreatmentPlanApiResource>(
    '/patients/' + patientId + '/treatment-plan/unshare'
  );
  return mapApiResourceToTreatmentPlan(response.data);
}

export async function fetchOwnTreatmentPlans(): Promise<ClientTreatmentPlan[]> {
  const response = await httpClient.get<ClientTreatmentPlanApiResource[]>('/me/treatment-plans');
  return response.data.map(mapApiResourceToClientTreatmentPlan);
}

export async function confirmExerciseDone(
  exerciseId: string,
  payload: ExerciseCompletionCreatePayload
): Promise<ClientTreatmentPlanExercise> {
  const response = await httpClient.post<ClientTreatmentPlanExerciseApiResource>(
    '/me/treatment-plans/exercises/' + exerciseId + '/completions',
    mapExerciseCompletionCreatePayload(payload)
  );
  return mapApiResourceToClientTreatmentPlanExercise(response.data);
}

export async function undoExerciseCompletion(
  exerciseId: string,
  completedOn: string
): Promise<ClientTreatmentPlanExercise> {
  const response = await httpClient.delete<ClientTreatmentPlanExerciseApiResource>(
    '/me/treatment-plans/exercises/' + exerciseId + '/completions/' + completedOn
  );
  return mapApiResourceToClientTreatmentPlanExercise(response.data);
}

export async function fetchExerciseCompliance(patientId: string): Promise<ExerciseCompliance[]> {
  const response = await httpClient.get<ExerciseComplianceApiResource[]>(
    '/patients/' + patientId + '/treatment-plan/exercise-compliance'
  );
  return response.data.map(mapApiResourceToExerciseCompliance);
}
